/* eslint-disable react/prop-types */
import { Drawer, Form, Button, Input, message, Row, Col, Select, Typography, Grid, Switch, InputNumber, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { validations } from '../utils/validation'; // Import validations
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { currency } from '../utils/utils';
import { useSettings } from '../Context/SettingsContext';
import { useOperatorStore } from '../store/useOperatorStore';
import { useFetchFactorySelect } from '../Pages/factories/Hooks';

const LEGACY_FACTORY_VALUE = '__legacy_factory__';

const { Title } = Typography;
const { useBreakpoint } = Grid;
const EditCustomerDrawer = ({ open, onClose, customer, onSave, isPending }) => {
  const { t } = useTranslation('users');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const freeTierEnabled = Form.useWatch('freeTierEnabled', form);
  const freeTierUnit = Form.useWatch('freeTierUnit', form);
  const idleFeeEnabled = Form.useWatch('idleFeeEnabled', form);
  const { settings } = useSettings();
  const { operatorId } = useOperatorStore();
  const { data: factories = [] } = useFetchFactorySelect({ operatorId });
  const companyIdleGrace =
    settings?.idleFeeGraceMinutes != null
      ? Number(settings.idleFeeGraceMinutes)
      : 10;
  const companyIdleRate =
    settings?.idleFeeRatePerMinute != null
      ? Number(settings.idleFeeRatePerMinute)
      : 0;

  const factoryOptions = useMemo(() => {
    const options = factories.map((item) => ({
      value: item._id,
      label: item.name ? `${item.number} — ${item.name}` : item.number,
    }));
    const hasFactoryId =
      customer?.factoryId &&
      factories.some((item) => String(item._id) === String(customer.factoryId));
    const matchedByNumber = customer?.factoryNumber
      ? factories.find(
          (item) =>
            String(item.number).toLowerCase() ===
            String(customer.factoryNumber).trim().toLowerCase()
        )
      : null;
    if (!hasFactoryId && !matchedByNumber && customer?.factoryNumber) {
      options.push({
        value: LEGACY_FACTORY_VALUE,
        label: customer.factoryNumber,
        disabled: true,
      });
    }
    return options;
  }, [factories, customer]);

  const resolveFactoryFormValue = () => {
    if (customer?.factoryId) return customer.factoryId;
    if (customer?.factoryNumber) {
      const match = factories.find(
        (item) =>
          String(item.number).toLowerCase() ===
          String(customer.factoryNumber).trim().toLowerCase()
      );
      if (match) return match._id;
      return LEGACY_FACTORY_VALUE;
    }
    return undefined;
  };

  useEffect(() => {
    if (open && customer) {
      form.setFieldsValue({
        fname: customer.fname,
        lname: customer.lname,
        phoneNumber: customer.phoneNumber,
        countryCode: customer.countryCode,
        tagId: customer.tagId,
        address: customer.address,
        employeeId: customer.employeeId || '',
        factoryId: resolveFactoryFormValue(),
        startChargeEnabled: !!customer.startChargeEnabled,
        monthlyReportEnabled: !!customer.monthlyReportEnabled,
        freeTierEnabled: !!customer.freeTierEnabled,
        freeTierUnit: customer.freeTierUnit || 'KWH',
        freeTierMonthlyThreshold: customer.freeTierMonthlyThreshold || 0,
        idleFeeEnabled: customer.idleFeeEnabled !== false,
        idleFeeRatePerMinute:
          customer.idleFeeRatePerMinute != null
            ? Number(customer.idleFeeRatePerMinute)
            : null,
        idleFeeGraceMinutes:
          customer.idleFeeGraceMinutes != null
            ? Number(customer.idleFeeGraceMinutes)
            : null,
      });
    }
  }, [open, customer, form, factories]);

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
          ...values,
          startChargeEnabled: values.startChargeEnabled === true,
          monthlyReportEnabled: values.monthlyReportEnabled === true,
          freeTierEnabled: !!values.freeTierEnabled,
          freeTierUnit: values.freeTierUnit || 'KWH',
          freeTierMonthlyThreshold: values.freeTierEnabled
            ? Number(values.freeTierMonthlyThreshold) || 0
            : 0,
          idleFeeEnabled: values.idleFeeEnabled !== false,
        };
        if (values.idleFeeEnabled !== false) {
          payload.idleFeeRatePerMinute = values.idleFeeRatePerMinute ?? null;
          payload.idleFeeGraceMinutes = values.idleFeeGraceMinutes ?? null;
        } else {
          payload.idleFeeRatePerMinute = null;
          payload.idleFeeGraceMinutes = null;
        }
        if (values.factoryId === LEGACY_FACTORY_VALUE) {
          delete payload.factoryId;
        } else if (!values.factoryId) {
          payload.factoryId = null;
        } else {
          payload.factoryId = values.factoryId;
        }
        delete payload.factoryNumber;
        onSave(payload);
      })
      .catch(() => {
        message.error(t('ValidationFailed'));
      });
  };

  return (
    <Drawer
      maskClosable={false}
      title={<Title level={4} className="!mb-0">{t('EditCustomer')}</Title>}
      width={isMobile ? '100%' : 600}
      onClose={onClose}
      open={open}
      classNames={{ body: { paddingBottom: 80 } }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          fname: customer?.fname,
          lname: customer?.lname,
          phoneNumber: customer?.phoneNumber,
          countryCode: customer?.countryCode,
          tagId: customer?.tagId,
          address: customer?.address,
          employeeId: customer?.employeeId || '',
          factoryId: customer?.factoryId,
          startChargeEnabled: !!customer?.startChargeEnabled,
          monthlyReportEnabled: !!customer?.monthlyReportEnabled,
          freeTierEnabled: !!customer?.freeTierEnabled,
          freeTierUnit: customer?.freeTierUnit || 'KWH',
          freeTierMonthlyThreshold: customer?.freeTierMonthlyThreshold || 0,
          idleFeeEnabled: customer?.idleFeeEnabled !== false,
          idleFeeRatePerMinute:
            customer?.idleFeeRatePerMinute != null
              ? Number(customer.idleFeeRatePerMinute)
              : null,
          idleFeeGraceMinutes:
            customer?.idleFeeGraceMinutes != null
              ? Number(customer.idleFeeGraceMinutes)
              : null,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="fname"
              label={t('FirstName')}
              rules={[
                validations.required(t('PleaseEnterFirstName')),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lname"
              label={t('LastName')}
              rules={[
                validations.required(t('PleaseEnterLastName')),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="phoneNumber"
              label={t('PhoneNumber')}
              rules={[
                validations.required(t('PleaseEnterPhoneNumber')),
                validations.noSpaces(t('NoWhitespaceAllowed')),
                {
                  pattern: /^\d{7,12}$/,
                  message: t('InvalidPhoneNumber'),
                },
              ]}
            >
              <Input
                disabled
                style={{ height: "38.5px" }}
                addonBefore={
                  <Form.Item
                    name="countryCode"
                    noStyle
                    rules={[
                      validations.required(t('PleaseEnterCountryCode')),
                      {
                        pattern: /^\d{1,4}$/,
                        message: t('InvalidCountryCode'),
                      },
                    ]}
                  >
                    +90
                  </Form.Item>
                }
              />
            </Form.Item>
          </Col>

        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="employeeId"
              label={t('EmployeeId')}
              rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
            >
              <Input placeholder={t('EnterEmployeeId')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="factoryId" label={t('FactoryNumber')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('SelectFactoryNumber')}
                options={factoryOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="tagId"
          label={t('TagId')}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={['address', 'line1']}
          label={t('AddressLine1')}
          rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]} // Updated message
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={['address', 'line2']}
          label={t('AddressLine2')}
          rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
        >
          <Input />
        </Form.Item>

        <Divider orientation="left">{t('StartCharge')}</Divider>
        <Form.Item
          name="startChargeEnabled"
          label={t('StartChargeEnabled')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider orientation="left">{t('MonthlyReport')}</Divider>
        <Form.Item
          name="monthlyReportEnabled"
          label={t('MonthlyReportEnabled')}
          valuePropName="checked"
          extra={t('MonthlyReportHelp')}
        >
          <Switch />
        </Form.Item>

        <Divider orientation="left">{t('FreeTier')}</Divider>
        <Form.Item
          name="freeTierEnabled"
          label={t('FreeTierEnabled')}
          valuePropName="checked"
          extra={t('FreeTierHelp')}
        >
          <Switch />
        </Form.Item>
        {freeTierEnabled && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="freeTierUnit"
                label={t('FreeTierUnit')}
                rules={[validations.required(t('FreeTierUnitRequired'))]}
              >
                <Select
                  options={[
                    { value: 'KWH', label: t('FreeTierUnitKwh') },
                    { value: 'CURRENCY', label: t('FreeTierUnitCurrency') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="freeTierMonthlyThreshold"
                label={t('FreeTierThreshold')}
                rules={[
                  validations.required(t('FreeTierThresholdRequired')),
                  {
                    type: 'number',
                    min: 0.01,
                    message: t('FreeTierThresholdMin'),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  addonAfter={freeTierUnit === 'CURRENCY' ? currency : 'kWh'}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Divider orientation="left">{t('IdleFee')}</Divider>
        <Form.Item
          name="idleFeeEnabled"
          label={t('IdleFeeEnabled')}
          valuePropName="checked"
          extra={t('IdleFeeEnabledHelp')}
        >
          <Switch />
        </Form.Item>
        {idleFeeEnabled !== false && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="idleFeeGraceMinutes"
                label={t('IdleFeeGraceLabel')}
                extra={t('IdleFeeOverrideHelp')}
                rules={[
                  { type: 'number', min: 2, message: t('IdleFeeGraceMin') },
                  {
                    type: 'number',
                    max: 1440,
                    message: t('IdleFeeGraceMax'),
                  },
                ]}
              >
                <InputNumber
                  min={2}
                  max={1440}
                  style={{ width: '100%' }}
                  placeholder={t('IdleFeeCompanyDefaultPlaceholder', {
                    value: companyIdleGrace,
                  })}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="idleFeeRatePerMinute"
                label={t('IdleFeeRateLabel')}
                extra={t('IdleFeeOverrideHelp')}
                rules={[
                  { type: 'number', min: 0, message: t('IdleFeeRateMin') },
                ]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  addonAfter={currency}
                  placeholder={t('IdleFeeCompanyDefaultPlaceholder', {
                    value: companyIdleRate,
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <div className="flex flex-row gap-2">
          <Button type="primary" loading={isPending} onClick={handleSave}>
            {t('Save')}
          </Button>
          <Button onClick={onClose}>{t('Cancel')}</Button>
        </div>
      </Form>
    </Drawer>
  );
};

EditCustomerDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.shape({
    fname: PropTypes.string,
    lname: PropTypes.string,
    phoneNumber: PropTypes.string,
    countryCode: PropTypes.string,
    address: PropTypes.object,
    employeeId: PropTypes.string,
    factoryId: PropTypes.string,
    factoryNumber: PropTypes.string,
    startChargeEnabled: PropTypes.bool,
    monthlyReportEnabled: PropTypes.bool,
    freeTierEnabled: PropTypes.bool,
    freeTierUnit: PropTypes.string,
    freeTierMonthlyThreshold: PropTypes.number,
    idleFeeEnabled: PropTypes.bool,
    idleFeeRatePerMinute: PropTypes.number,
    idleFeeGraceMinutes: PropTypes.number,
  }),
  onSave: PropTypes.func.isRequired,
  isPending: PropTypes.bool,
};

export default EditCustomerDrawer;
